(() => {
  if (window.TreedixHeroMobileCarouselFix) return;

  const MOBILE_QUERY = '(max-width: 860px)';
  const ROLE_CLASSES = ['is-main', 'is-right', 'is-left', 'is-far-right', 'is-far-left', 'is-hidden'];
  const dragStartThreshold = 10;
  const fixedSwipeThreshold = 50;
  const velocityThreshold = 0.55;
  const stateMap = new WeakMap();
  const mediaQuery = window.matchMedia(MOBILE_QUERY);
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function normalizeIndex(index, length) {
    return (index + length) % length;
  }

  function classForOffset(offset, length) {
    if (offset === 0) return 'is-main';
    if (offset === 1) return 'is-right';
    if (offset === length - 1) return 'is-left';
    if (offset === 2) return 'is-far-right';
    if (offset === length - 2) return 'is-far-left';
    return 'is-hidden';
  }

  function getSlideTranslate(state, index) {
    const slide = state.slides[index];
    if (!slide) return 0;
    return -slide.offsetLeft + (state.cinema.clientWidth - slide.offsetWidth) / 2 - state.track.offsetLeft;
  }

  function applyTransform(state, x, animate) {
    state.track.classList.toggle('is-no-transition', !animate || reducedMotionQuery.matches);
    state.currentTranslate = x;
    state.track.style.setProperty('--treedix-mobile-hero-x', `${x}px`);
    state.cinema.scrollLeft = 0;
  }

  function setRoles(state, index) {
    state.currentIndex = normalizeIndex(index, state.slides.length);
    state.slides.forEach((slide, slideIndex) => {
      slide.classList.remove(...ROLE_CLASSES);
      const offset = normalizeIndex(slideIndex - state.currentIndex, state.slides.length);
      slide.classList.add(classForOffset(offset, state.slides.length));
      slide.setAttribute('aria-current', slideIndex === state.currentIndex ? 'true' : 'false');
    });

    if (state.primaryLink) {
      const activeSlide = state.slides[state.currentIndex];
      state.primaryLink.href = activeSlide.dataset.productUrl || activeSlide.getAttribute('href') || '/collections/all';
    }
  }

  function placeArrows(state) {
    const host = state.cinema.closest('.hero-product-stage') || state.cinema.parentElement;
    if (!host) return;

    if (state.prevButton && state.prevButton.parentElement !== host) host.appendChild(state.prevButton);
    if (state.nextButton && state.nextButton.parentElement !== host) host.appendChild(state.nextButton);

    host.style.setProperty('--treedix-hero-mobile-arrow-top', `${state.cinema.offsetTop + state.cinema.clientHeight / 2}px`);
  }

  function goToSlide(state, index, animate = true) {
    if (!state.slides.length) return;
    window.cancelAnimationFrame(state.raf);
    state.dragOffsetX = 0;
    state.isDragging = false;
    state.isAnimating = !!animate;
    state.cinema.classList.remove('is-mobile-dragging');
    placeArrows(state);
    setRoles(state, index);
    applyTransform(state, getSlideTranslate(state, state.currentIndex), animate);
    window.clearTimeout(state.animationTimer);
    state.animationTimer = window.setTimeout(() => {
      state.isAnimating = false;
      applyTransform(state, getSlideTranslate(state, state.currentIndex), false);
    }, animate && !reducedMotionQuery.matches ? 360 : 0);
  }

  function pauseAuto(state) {
    window.clearInterval(state.autoTimer);
    window.clearTimeout(state.resumeTimer);
  }

  function startAuto(state) {
    window.clearInterval(state.autoTimer);
    if (reducedMotionQuery.matches || state.slides.length < 2 || document.hidden) return;
    state.autoTimer = window.setInterval(() => {
      goToSlide(state, state.currentIndex + 1, true);
    }, 4500);
  }

  function scheduleAuto(state) {
    window.clearTimeout(state.resumeTimer);
    if (reducedMotionQuery.matches || state.slides.length < 2) return;
    state.resumeTimer = window.setTimeout(() => startAuto(state), 5500);
  }

  function restoreButton(button, parent, sibling) {
    if (!button || !parent) return;
    parent.insertBefore(button, sibling && sibling.parentNode === parent ? sibling : null);
  }

  function init(root = document) {
    if (!document.body.classList.contains('template-index') || !mediaQuery.matches) return;

    if (window.TreedixHeroShowcase && typeof window.TreedixHeroShowcase.destroy === 'function') {
      window.TreedixHeroShowcase.destroy(root === document ? document : root);
    }

    root.querySelectorAll('[data-hero-cinema]').forEach((cinema) => {
      if (stateMap.has(cinema)) return;

      const track = cinema.querySelector('.tl-hero-cinema__track');
      const slides = track ? Array.from(track.querySelectorAll('.tl-hero-showcase-card')) : [];
      if (!track || !slides.length) return;

      const prevButton = cinema.querySelector('[data-hero-prev]');
      const nextButton = cinema.querySelector('[data-hero-next]');
      const initialIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-main')));
      const state = {
        cinema,
        track,
        slides,
        prevButton,
        nextButton,
        prevOriginalParent: prevButton ? prevButton.parentNode : null,
        prevOriginalSibling: prevButton ? prevButton.nextSibling : null,
        nextOriginalParent: nextButton ? nextButton.parentNode : null,
        nextOriginalSibling: nextButton ? nextButton.nextSibling : null,
        primaryLink: (cinema.closest('.tl-homepage') || document).querySelector('[data-hero-primary-link]'),
        currentIndex: initialIndex,
        currentTranslate: 0,
        baseTranslateX: 0,
        dragOffsetX: 0,
        isDragging: false,
        isAnimating: false,
        down: false,
        horizontal: false,
        vertical: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        lastX: 0,
        lastTime: 0,
        velocityX: 0,
        raf: 0,
        pendingTranslate: 0,
        suppressClickUntil: 0,
        autoTimer: 0,
        resumeTimer: 0,
        animationTimer: 0,
        cleanup: []
      };

      stateMap.set(cinema, state);
      placeArrows(state);

      slides.forEach((slide) => {
        slide.querySelectorAll('img').forEach((img) => {
          img.setAttribute('draggable', 'false');
        });
      });

      function renderDrag() {
        applyTransform(state, state.pendingTranslate, false);
        state.raf = 0;
      }

      function onPointerDown(event) {
        if (event.button !== undefined && event.button !== 0) return;
        if (event.target.closest('button, .tl-btn, [data-hero-primary-link], [data-hero-prev], [data-hero-next]')) return;

        pauseAuto(state);
        window.clearTimeout(state.animationTimer);
        state.down = true;
        state.horizontal = false;
        state.vertical = false;
        state.isDragging = false;
        state.pointerId = event.pointerId;
        state.startX = event.clientX;
        state.startY = event.clientY;
        state.baseTranslateX = getSlideTranslate(state, state.currentIndex);
        state.dragOffsetX = 0;
        state.lastX = event.clientX;
        state.lastTime = performance.now();
        state.velocityX = 0;
        state.suppressClickUntil = 0;
        applyTransform(state, state.baseTranslateX, false);
      }

      function onPointerMove(event) {
        if (!state.down || event.pointerId !== state.pointerId) return;

        const deltaX = event.clientX - state.startX;
        const deltaY = event.clientY - state.startY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        const now = performance.now();
        const dt = Math.max(1, now - state.lastTime);
        state.velocityX = (event.clientX - state.lastX) / dt;
        state.lastX = event.clientX;
        state.lastTime = now;

        if (!state.horizontal && !state.vertical && Math.max(absX, absY) >= dragStartThreshold) {
          if (absX > absY * 1.15) {
            state.horizontal = true;
            state.isDragging = true;
            state.cinema.classList.add('is-mobile-dragging');
            try {
              state.cinema.setPointerCapture(event.pointerId);
            } catch (error) {}
          } else if (absY > absX) {
            state.vertical = true;
            state.down = false;
            scheduleAuto(state);
            return;
          }
        }

        if (!state.horizontal) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        state.dragOffsetX = deltaX;
        state.pendingTranslate = state.baseTranslateX + state.dragOffsetX;
        if (!state.raf) state.raf = window.requestAnimationFrame(renderDrag);
      }

      function finishPointer(event) {
        if (!state.down || event.pointerId !== state.pointerId) return;

        const deltaX = event.clientX - state.startX;
        const absX = Math.abs(deltaX);
        const swipeThreshold = Math.min(fixedSwipeThreshold, state.cinema.clientWidth * 0.12);
        const fastSwipe = absX >= 24 && Math.abs(state.velocityX) >= velocityThreshold;
        let targetIndex = state.currentIndex;

        if (state.horizontal && (absX >= swipeThreshold || fastSwipe)) {
          targetIndex = deltaX < 0 ? state.currentIndex + 1 : state.currentIndex - 1;
          state.suppressClickUntil = Date.now() + 320;
        } else if (state.horizontal) {
          targetIndex = state.currentIndex;
          state.suppressClickUntil = Date.now() + 180;
        }

        state.down = false;
        state.horizontal = false;
        state.vertical = false;
        state.isDragging = false;
        state.dragOffsetX = 0;
        state.cinema.classList.remove('is-mobile-dragging');
        window.cancelAnimationFrame(state.raf);
        state.raf = 0;
        try {
          state.cinema.releasePointerCapture(event.pointerId);
        } catch (error) {}

        goToSlide(state, targetIndex, true);
        scheduleAuto(state);
      }

      function onPointerCancel(event) {
        if (!state.down || event.pointerId !== state.pointerId) return;
        state.down = false;
        state.horizontal = false;
        state.vertical = false;
        state.isDragging = false;
        state.dragOffsetX = 0;
        state.cinema.classList.remove('is-mobile-dragging');
        goToSlide(state, state.currentIndex, true);
        scheduleAuto(state);
      }

      function onClick(event) {
        if (Date.now() >= state.suppressClickUntil) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }

      function onPrevClick(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        pauseAuto(state);
        goToSlide(state, state.currentIndex - 1, true);
        scheduleAuto(state);
      }

      function onNextClick(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        pauseAuto(state);
        goToSlide(state, state.currentIndex + 1, true);
        scheduleAuto(state);
      }

      function onResize() {
        placeArrows(state);
        goToSlide(state, state.currentIndex, false);
      }

      function onVisibilityChange() {
        if (document.hidden) pauseAuto(state);
        else scheduleAuto(state);
      }

      function onImageLoad() {
        goToSlide(state, state.currentIndex, false);
      }

      state.cinema.addEventListener('pointerdown', onPointerDown, { capture: true });
      state.cinema.addEventListener('pointermove', onPointerMove, { capture: true });
      state.cinema.addEventListener('pointerup', finishPointer, { capture: true });
      state.cinema.addEventListener('pointercancel', onPointerCancel, { capture: true });
      state.cinema.addEventListener('click', onClick, { capture: true });
      if (state.prevButton) state.prevButton.addEventListener('click', onPrevClick, { capture: true });
      if (state.nextButton) state.nextButton.addEventListener('click', onNextClick, { capture: true });
      window.addEventListener('resize', onResize, { passive: true });
      window.addEventListener('orientationchange', onResize, { passive: true });
      document.addEventListener('visibilitychange', onVisibilityChange);
      slides.forEach((slide) => slide.querySelectorAll('img').forEach((img) => img.addEventListener('load', onImageLoad, { once: true })));
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(onResize).catch(() => {});

      state.cleanup.push(() => {
        state.cinema.removeEventListener('pointerdown', onPointerDown, { capture: true });
        state.cinema.removeEventListener('pointermove', onPointerMove, { capture: true });
        state.cinema.removeEventListener('pointerup', finishPointer, { capture: true });
        state.cinema.removeEventListener('pointercancel', onPointerCancel, { capture: true });
        state.cinema.removeEventListener('click', onClick, { capture: true });
        if (state.prevButton) state.prevButton.removeEventListener('click', onPrevClick, { capture: true });
        if (state.nextButton) state.nextButton.removeEventListener('click', onNextClick, { capture: true });
        window.removeEventListener('resize', onResize);
        window.removeEventListener('orientationchange', onResize);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        restoreButton(state.prevButton, state.prevOriginalParent, state.prevOriginalSibling);
        restoreButton(state.nextButton, state.nextOriginalParent, state.nextOriginalSibling);
        state.track.style.removeProperty('--treedix-mobile-hero-x');
        state.track.classList.remove('is-no-transition');
      });

      window.requestAnimationFrame(() => {
        goToSlide(state, state.currentIndex, false);
        scheduleAuto(state);
      });
    });
  }

  function destroy(root = document) {
    root.querySelectorAll('[data-hero-cinema]').forEach((cinema) => {
      const state = stateMap.get(cinema);
      if (!state) return;
      pauseAuto(state);
      window.clearTimeout(state.animationTimer);
      window.cancelAnimationFrame(state.raf);
      state.cleanup.forEach((callback) => callback());
      cinema.classList.remove('is-mobile-dragging');
      stateMap.delete(cinema);
    });
  }

  function refresh(root = document) {
    if (mediaQuery.matches) {
      init(root);
      return;
    }
    destroy(root);
    if (window.TreedixHeroShowcase && typeof window.TreedixHeroShowcase.init === 'function') {
      window.TreedixHeroShowcase.init(root);
    }
  }

  window.TreedixHeroMobileCarouselFix = { init, destroy, refresh };
  refresh(document);
  mediaQuery.addEventListener('change', () => refresh(document));
  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
